import { supabase } from './supabase';

/**
 * Coach quick-rate: insert four rating rows (one per corner) for a player
 * in REVEALED state — web2 is coach-only for now (the blind state machine
 * ships when player self-rating lands). All rows share a single created_at
 * so they can be grouped later.
 *
 * Optional `subScores` lets the coach drill into sub-corner detail (e.g.
 * specifically rating "First Touch" within Technical). Each entry inserts
 * an additional rating row with both corner_id and sub_corner_id set.
 */

const CORNER_IDS = {
  tech:   'corner-technical',
  phys:   'corner-physical',
  psych:  'corner-psych',
  social: 'corner-social',
} as const;

export type CornerKey = keyof typeof CORNER_IDS;

export interface RateScores {
  tech: number;
  phys: number;
  psych: number;
  social: number;
}

export interface SubCornerSpec {
  id: string;
  label: string;
}

/**
 * Curated sub-corners exposed in the rate UI. IDs are stable string keys
 * so demo data and Supabase rows agree without needing a UUID lookup. The
 * label list mirrors the §00 four-corner spec.
 */
export const SUB_CORNERS_BY_KEY: Record<CornerKey, ReadonlyArray<SubCornerSpec>> = {
  tech: [
    { id: 'tech.first-touch',     label: 'First Touch' },
    { id: 'tech.passing',         label: 'Passing' },
    { id: 'tech.finishing',       label: 'Finishing' },
    { id: 'tech.dribbling',       label: 'Dribbling' },
    { id: 'tech.decision-making', label: 'Decision-Making' },
    { id: 'tech.scanning',        label: '360° Awareness' },
    { id: 'tech.pressing',        label: 'Pressing' },
  ],
  phys: [
    { id: 'phys.speed',         label: 'Speed' },
    { id: 'phys.agility',       label: 'Agility' },
    { id: 'phys.strength',      label: 'Strength' },
    { id: 'phys.endurance',     label: 'Endurance' },
    { id: 'phys.reaction-time', label: 'Reaction Time' },
  ],
  psych: [
    { id: 'psych.confidence',   label: 'Confidence' },
    { id: 'psych.composure',    label: 'Composure' },
    { id: 'psych.resilience',   label: 'Resilience' },
    { id: 'psych.coachability', label: 'Coachability' },
  ],
  social: [
    { id: 'social.communication', label: 'Communication' },
    { id: 'social.teamwork',      label: 'Teamwork' },
    { id: 'social.leadership',    label: 'Leadership' },
  ],
};

const SUB_TO_CORNER: Record<string, CornerKey> = (() => {
  const out: Record<string, CornerKey> = {};
  (Object.keys(SUB_CORNERS_BY_KEY) as CornerKey[]).forEach((k) => {
    SUB_CORNERS_BY_KEY[k].forEach((s) => { out[s.id] = k; });
  });
  return out;
})();

interface SaveOptions {
  coachId: string;
  playerId: string;
  scores: RateScores;
  /** Optional: map of sub_corner_id → score (0-100). */
  subScores?: Record<string, number>;
  sessionId?: string | null;
  context?: 'SESSION' | 'GAME';
}

export async function saveCornerRatings(
  opts: SaveOptions,
): Promise<{ ok: true; ids: string[] } | { ok: false; error: string }> {
  const { coachId, playerId, scores, subScores = {}, sessionId = null, context = 'SESSION' } = opts;
  const now = new Date().toISOString();

  const cornerRows = (Object.keys(CORNER_IDS) as CornerKey[]).map((key) => ({
    rater_id: coachId,
    ratee_id: playerId,
    ratee_type: 'PLAYER' as const,
    context,
    session_id: sessionId,
    corner_id: CORNER_IDS[key],
    sub_corner_id: null as string | null,
    score: clamp(scores[key]),
    is_blind: false,
    state: 'REVEALED' as const,
    revealed_at: now,
  }));

  const subRows = Object.entries(subScores)
    .filter(([subId]) => SUB_TO_CORNER[subId])
    .map(([subId, score]) => ({
      rater_id: coachId,
      ratee_id: playerId,
      ratee_type: 'PLAYER' as const,
      context,
      session_id: sessionId,
      corner_id: CORNER_IDS[SUB_TO_CORNER[subId]],
      sub_corner_id: subId,
      score: clamp(score),
      is_blind: false,
      state: 'REVEALED' as const,
      revealed_at: now,
    }));

  const rows = [...cornerRows, ...subRows];

  const { data, error } = await supabase
    .from('ratings')
    .insert(rows)
    .select('id');
  if (error) return { ok: false, error: error.message };
  const ids = ((data ?? []) as { id: string }[]).map((r) => r.id);
  return { ok: true, ids };
}

function clamp(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}
